/**
 * Advisory Configuration
 *
 * The supervisor endpoint URL and the request-wide `schemaVersion` literal,
 * validated at module load exactly as `chat/chatConfig.ts:8-13` does. This is
 * the ONLY home for `schemaVersion` in this codebase — it must never be
 * typed or hardcoded at a call site.
 *
 * `ADVISORY_SCHEMA_VERSION` provenance (verified 2026-08-09):
 *   - `agent-svc origin/development:app/features/supervisor/schemas.py:54` —
 *     `SchemaVersion = Literal["v2", "v3"]`; `schema_version: SchemaVersion = "v3"`
 *   - `gmid:app/features/agent/schemas.py:105` —
 *     `SupervisorCreateRequest.schema_version: SchemaVersion = "v3"`
 * An earlier draft used a `'1'` placeholder for this value. That placeholder
 * is WRONG and superseded by the verification above — do not "restore" it
 * in a future edit.
 */

// Backend URL validation and construction (mirrors chatConfig.ts:8-13)
const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_BASE_URL;

if (!BACKEND_BASE_URL) {
  throw new Error('REACT_APP_BACKEND_BASE_URL environment variable is required but not set');
}

/**
 * The supervisor turn endpoint. gmid proxies this verbatim to agent-svc's SSE
 * response; gmid injects `userId` from the validated Firebase JWT, so the
 * client must never send `userId` on this wire.
 */
export const ADVISORY_SUPERVISOR_ENDPOINT = `${BACKEND_BASE_URL}/api/v1/agent/supervisor`;

/**
 * The three docintel document routes, through gmid.
 *
 * Templated as functions rather than assembled at the call site so the paths exist
 * in exactly one place — the ingest and status routes take the caller-minted
 * `document_id` as a path segment, and two hand-built template strings drift.
 *
 * gmid gates these behind `ENABLE_DOCINTEL_DOCUMENTS` (on in dev, off in
 * production), so a 404 here is a feature flag rather than a wrong path.
 */
export const ADVISORY_DOCUMENT_ENDPOINTS = {
  chunks: `${BACKEND_BASE_URL}/api/v1/documents/docintel/chunks`,
  upload: `${BACKEND_BASE_URL}/api/v1/documents/docintel-upload`,
  ingest: (documentId: string): string =>
    `${BACKEND_BASE_URL}/api/v1/documents/docintel/${encodeURIComponent(documentId)}/ingest`,
  ingestStatus: (documentId: string): string =>
    `${BACKEND_BASE_URL}/api/v1/documents/docintel/${encodeURIComponent(documentId)}/ingest-status`,
} as const;

/**
 * Literal-typed (not `string`) so a future edit to an unsupported value is a
 * compile error at the call site rather than a runtime rejection by agent-svc.
 */
export const ADVISORY_SCHEMA_VERSION = 'v3' as const;

/**
 * The supervisor request DTO's real, complete field list — verified live
 * against `agent-svc@47c8f03 app/features/supervisor/schemas.py::SupervisorRunRequest`
 * (the S-05 merge, #73), in the DTO's own declaration order. `userId` is
 * deliberately absent: gmid injects it from the validated Firebase JWT and the
 * client must never send it (see `ADVISORY_SUPERVISOR_ENDPOINT`'s docblock above).
 *
 * Every field below also has to survive **gmid**, which is a second place a
 * field can vanish silently. Its `SupervisorCreateRequest` is `extra="ignore"`
 * too, and the proxy forwards `body.model_dump(...)` — the PARSED model — so a
 * field gmid does not declare cannot reach agent-svc even when this client sends
 * it correctly. The seven added here are declared on both.
 *
 * The DTO's `model_config` sets `extra="ignore"` — an unknown field produces
 * NO error and NO effect; it is silently dropped on arrival. That is exactly
 * the gap that let three dead fields (`addCriterion`, `addKeyQuestion`,
 * `removeSubQuestion`) ship undetected in an earlier request body with every
 * test in the suite green. This const is the reason a field outside it is
 * now a COMPILE ERROR (`TS2353`, via `AdvisorySupervisorRequestBody` below)
 * rather than a silent no-op. `advisoryConfig.test.ts`'s equality assertion
 * fails, naming the missing field, the moment this array drifts from the
 * real DTO.
 */
export const ADVISORY_SUPERVISOR_REQUEST_FIELDS = [
  'projectId',
  'userQuery',
  'refinement',
  'confirm',
  'classify',
  'removeQuestions',
  'addQuestion',
  'confirmQuestions',
  'acceptQuestion',
  'confirmCompany',
  'companyName',
  'confirmPeriod',
  'period',
  'useCurrentYear',
  'review',
  'workspace',
  'confirmCounterparties',
  'counterparties',
  'retryCounterpartyProfile',
  'counterparty',
  'counterpartyUrl',
  'prepareAnalysis',
  'startAnalysis',
  'startAnswers',
  'answersRerunMode',
  'analysisDebug',
  'analysisMaxKeyQuestions',
  'analysisMaxSubquestions',
  'analysisNodeProfile',
  'schemaVersion',
  'messageId',
  'conversationType',
] as const;

/** Union of the supervisor field-name string literals — never widened to `string`. */
export type AdvisorySupervisorRequestField = (typeof ADVISORY_SUPERVISOR_REQUEST_FIELDS)[number];

/**
 * Constrains KEYS only, not value types — value typing stays on the
 * caller-facing request interface (`SupervisorTurnRequest` in
 * `useSupervisorTurn.ts`). `projectId` is required (every supervisor turn
 * addresses a project); the remaining nineteen fields are optional. Because
 * `AdvisorySupervisorRequestField` is a closed union of string literals
 * rather than an index signature, an object literal assigned to this type
 * with any key outside the twenty is a compile error (`TS2353`) — the
 * mechanism this codebase's DTO-drift tests rely on.
 */
export type AdvisorySupervisorRequestBody = {
  projectId: string;
} & {
  [K in Exclude<AdvisorySupervisorRequestField, 'projectId'>]?: unknown;
};
